/**
 * Terms of Service Page
 *
 * Public page (no authentication required)
 * Static content for legal compliance
 */

import React from "react";
import { PublicLayout } from "../components/public/PublicLayout";
import "./LegalPages.css";

export function TermsPage(): JSX.Element {
  return (
    <PublicLayout>
      <div className="legal-page">
      <div className="legal-page__container">
        <h1>Terms of Service</h1>
        <p className="legal-page__last-updated">Last Updated: August 23, 2026</p>

        <section className="legal-page__section">
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing or using Zakyam AI Trader ("the Service", "we", "us", "our"), you accept and agree to be bound by these Terms of Service, our Privacy Policy, and our Risk Disclaimer. If you do not agree with any part of these terms, you must not use the Service.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>2. Eligibility</h2>
          <p>
            You must be at least 18 years old and legally permitted to use cryptocurrency trading services in your jurisdiction. You are solely responsible for determining whether trading is lawful for you and for complying with all applicable laws and regulations.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>3. Nature of the Service</h2>
          <p>
            The Service provides automated trading tools that generate trade opportunities based on AI and technical signals, and execute them on your connected exchange account. You acknowledge and agree that:
          </p>
          <ul>
            <li>The Service is a technology tool, not a financial advisor, broker, or portfolio manager</li>
            <li>No fiduciary, advisory, or trust relationship is created between you and the Service</li>
            <li>The Service does not provide personalized financial, investment, legal, or tax advice</li>
            <li>You, and not the Service, are solely responsible for your trading decisions and their outcomes, including any losses</li>
          </ul>
          <p>
            <strong>Automated execution without per-trade approval:</strong> When automated trading is
            enabled, the Service places orders based on its automated signals WITHOUT further per-trade
            approval from you. By enabling automated trading, you consent in advance to this automated
            execution. You may disable automated trading at any time.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>4. No Guarantee of Profits</h2>
          <p>
            <strong>The Service does not guarantee profits, returns, or any specific performance.</strong> Any figures, statistics, or results referenced on this site reflect historical backtesting or simulated performance over past periods, and are provided for illustrative purposes only. Such results include losing trades, do not account for all real-world conditions (such as live slippage, liquidity constraints, or exchange fees), and are not a promise, target, or prediction of future results.
          </p>
          <p>
            Trading cryptocurrencies involves substantial risk of loss. You may lose some or all of the capital you allocate. Never trade with funds you cannot afford to lose.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>5. API Keys and Security</h2>
          <p>
            You are responsible for maintaining the confidentiality of your API keys. The Service requires Trade-only API keys (no Withdraw permission). You agree to:
          </p>
          <ul>
            <li>Never share your API keys with third parties</li>
            <li>Use only Trade permissions (no Withdraw)</li>
            <li>Enable IP whitelisting where available</li>
            <li>Report any unauthorized access immediately</li>
          </ul>
        </section>

        <section className="legal-page__section">
          <h2>6. Prohibited Conduct</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Service for any unlawful purpose</li>
            <li>Attempt to gain unauthorized access to the Service or its systems</li>
            <li>Reverse engineer, decompile, or extract the Service's software</li>
            <li>Interfere with the Service's operation or other users' use of the Service</li>
            <li>Use the Service in a jurisdiction where it is prohibited</li>
          </ul>
        </section>

        <section className="legal-page__section">
          <h2>7. Disclaimer of Warranties</h2>
          <p>
            The Service is provided on an "AS-IS" and "AS-AVAILABLE" basis, without warranties of any kind, whether express or implied, including but not limited to merchantability, fitness for a particular purpose, non-infringement, and accuracy. We do not warrant that the Service will be uninterrupted, error-free, secure, or profitable.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>8. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, Zakyam AI Trader and its owners, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of profits, data, or capital, arising out of or related to your use of, or inability to use, the Service.
          </p>
          <p>
            In no event shall our total aggregate liability exceed the total subscription fees actually paid by you in the three (3) months preceding the event giving rise to the claim. Some jurisdictions do not allow the exclusion or limitation of certain damages, so some or all of the above limitations may not apply to you.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>9. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless Zakyam AI Trader, its owners, directors, employees, and agents from and against any claims, losses, liabilities, damages, costs, and expenses (including reasonable attorneys' fees) arising out of or related to your use of the Service, your breach of these Terms, your trading activity, or your violation of any law or the rights of any third party.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>10. Force Majeure</h2>
          <p>
            We shall not be liable for any failure or delay in performance caused by circumstances beyond our reasonable control, including but not limited to exchange outages, API downtime, network or internet failures, market suspensions, regulatory actions, natural disasters, or other events of force majeure.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>11. Termination</h2>
          <p>
            We may suspend or terminate your access to the Service at any time, with or without cause, for violation of these Terms or any applicable law. Termination does not entitle you to a refund, except as provided in our Refund Policy. You may stop using the Service at any time.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>12. Governing Law and Dispute Resolution</h2>
          <p>
            These Terms are governed by and construed in accordance with the laws of the jurisdiction in which the Service operator is legally incorporated, without regard to its conflict of law provisions. Any dispute shall first be resolved through good-faith negotiation. If negotiation fails, the dispute shall be resolved through binding arbitration in the operator's jurisdiction of incorporation. You agree to waive any right to participate in a class action or class-wide arbitration.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>13. Authorization &amp; Limited Agency</h2>
          <p>
            By connecting your Exchange Account and enabling automated trading, you authorize the Service to place orders on your Exchange Account via API, using your own API credentials, with Trade-only permissions (no Withdraw).
          </p>
          <ul>
            <li>This authorization is strictly limited to order placement on your own account</li>
            <li>It does NOT include authority to withdraw, transfer, or otherwise take custody of your funds</li>
            <li>You may revoke this authorization at any time by disabling automated trading or deleting your API key</li>
            <li>The Service never holds, controls, or has custody of your funds; your funds remain on your Exchange account at all times</li>
          </ul>
        </section>

        <section className="legal-page__section">
          <h2>14. User Control &amp; Non-Discretionary Nature</h2>
          <p>
            You retain full control of your Exchange Account at all times. Automated trading may be enabled or disabled by you at any time from your account settings. When disabled, no new automated orders will be placed on your account.
          </p>
          <p>
            You own your API credentials and may revoke or delete them at any time, which immediately stops the Service's access to your account. The Service provides automated execution tools, not discretionary portfolio management. No fiduciary, advisory, or trust relationship is created.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>15. No Investment Advice</h2>
          <p>
            All signals and executions are general and automated, and are not tailored to your personal financial situation, objectives, or risk tolerance. The Service does not provide personalized financial, investment, legal, or tax advice. You should consult a qualified professional before making any trading decisions.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>16. Exchange Terms &amp; Account Restrictions</h2>
          <p>
            Each exchange has its own Terms of Service and API Terms. You are solely responsible for complying with the terms of your chosen exchange.
          </p>
          <ul>
            <li>The Service is not responsible if an exchange restricts, suspends, or terminates your account or your access to automation, nor for any losses arising from such actions</li>
            <li>You acknowledge and accept the risk that using third-party automation may, in some jurisdictions or circumstances, conflict with your exchange's terms, and you assume that risk</li>
          </ul>
        </section>

        <section className="legal-page__section">
          <h2>17. Regulatory Status Disclosure</h2>
          <p>
            The Service is a technology and software provider. It is not a licensed broker, dealer, investment adviser, or portfolio manager in any jurisdiction, and nothing in these Terms shall be construed as such. You are responsible for determining whether your use of the Service is lawful in your jurisdiction.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>18. Changes to These Terms</h2>
          <p>
            We may revise these Terms at any time. We will notify you of material changes by posting the updated Terms on this page and updating the "Last Updated" date. Your continued use of the Service after such changes constitutes your acceptance of the revised Terms.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>19. Contact Information</h2>
          <p>
            If you have any questions about these Terms of Service, please contact us through the Support page.
          </p>
        </section>
      </div>
      </div>
    </PublicLayout>
  );
}

export default TermsPage;
